/** @typedef {{ name: string; email: string; phone: string; address: string; city: string; postalCode: string; country: string }} OrderInvoiceBilling */

const STORAGE_PREFIX = 'orderInvoiceBilling:';

/**
 * @param {string | null | undefined} orderId
 * @param {OrderInvoiceBilling} billing
 */
export function saveOrderInvoiceBilling(orderId, billing) {
  if (!orderId || !billing) return;
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${orderId}`, JSON.stringify(billing));
  } catch {
    // Ignore storage quota or privacy mode errors.
  }
}

/**
 * @param {string | null | undefined} orderId
 * @returns {OrderInvoiceBilling | null}
 */
export function loadOrderInvoiceBilling(orderId) {
  if (!orderId) return null;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${orderId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizeOrderInvoiceBilling(parsed);
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, string | undefined> | null | undefined} source
 * @returns {OrderInvoiceBilling | null}
 */
function normalizeOrderInvoiceBilling(source) {
  if (!source || typeof source !== 'object') return null;

  const billing = {
    name: String(source.name || source.fullName || '').trim(),
    email: String(source.email || '').trim(),
    phone: String(source.phone || '').trim(),
    address: String(source.address || source.addressLine1 || source.line1 || '').trim(),
    city: String(source.city || '').trim(),
    postalCode: String(source.postalCode || source.postal_code || source.zip || '').trim(),
    country: String(source.country || '').trim(),
  };

  if (!billing.name && !billing.email) {
    return null;
  }

  return billing;
}

/**
 * @param {Record<string, string | undefined> | null | undefined} order
 * @returns {OrderInvoiceBilling | null}
 */
function extractBillingFromOrder(order) {
  if (!order || typeof order !== 'object') return null;
  const sources = [order.facturacion, order.billingDetails, order.billing, order.customer];
  for (const source of sources) {
    const billing = normalizeOrderInvoiceBilling(source);
    if (billing) return billing;
  }
  return null;
}

/**
 * @param {string | null | undefined} orderId
 * @param {Record<string, string | undefined> | null | undefined} order
 * @param {OrderInvoiceBilling | null | undefined} billingFromState
 * @returns {OrderInvoiceBilling | null}
 */
export function resolveOrderInvoiceBilling(orderId, order, billingFromState) {
  return (
    normalizeOrderInvoiceBilling(billingFromState)
    || loadOrderInvoiceBilling(orderId)
    || extractBillingFromOrder(order)
  );
}
